/** Category lookups and small building blocks of the request pages. */
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { CategoryNodeResponse } from "@offertli/shared";
import { apiGet } from "../../lib/api.js";
import { cx } from "../../components/ui/cx.js";

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: () =>
      apiGet<{ data: CategoryNodeResponse[] }>("/settings/categories").then(
        (response) => response.data,
      ),
    staleTime: 5 * 60_000,
  });
}

export function useCategoryName() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.split("-")[0];
  return (category: CategoryNodeResponse) =>
    (locale === "de" || locale === "fr" || locale === "es"
      ? category.translations[locale]
      : undefined) ?? category.name;
}

export function Chips<V extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: V | "";
  options: ReadonlyArray<{ value: V; label: ReactNode }>;
  onChange: (value: V | "") => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(selected ? "" : option.value)}
            className={cx(
              "min-h-11 rounded-full border px-4 py-2 text-sm transition-colors",
              selected
                ? "border-brand bg-brand text-white"
                : "border-app-border bg-app-surface text-app-text hover:border-brand",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-app-border bg-app-surface p-4 sm:p-5">
      <h2 className="mb-4 flex items-center gap-2 text-base font-semibold">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand text-sm text-white">
          {number}
        </span>
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* List                                                                */
/* ------------------------------------------------------------------ */
